<template>
  <div id="app">
    <template v-if="isOnline">
      <AppLoading v-if="!loaded || pageChanging" />
      <router-view v-show="!pageChanging" />
      <!-- <Layout v-show="loaded && !pageChanging" /> -->
      <Commander
        v-if="$store.state.app.commander"
        @close="$store.commit('app/SHOW_COMMANDER', false)"
      />
      <FeatureKeyDebugger v-if="loaded && usePermissionsDebugger" />
      <FeatureKeyInspector v-if="loaded && usePermissionsInspector" />
      <UnsavedLeaveDialog />

      <!-- 版本更新通知對話框 -->
      <WarningDialog
        v-if="showUpdateDialog"
        title="系統更新"
        :content="`檢測到新版本 ${newVersion}，建議立即更新以獲得更好的使用體驗`"
        btn-string="立即更新"
        cancel-btn-string="稍後再說"
        @confirm="handleVersionRefresh"
        @close="handleVersionCancel"
      />
    </template>
    <NoNetwork v-else />
  </div>
</template>

<script>
import {
  defineComponent,
  computed,
  onBeforeMount,
  ref,
  onMounted,
  nextTick,
} from 'vue'
import { mapGetters } from 'vuex'
import ls from '@/utils/localstorage'
import configuration from '@/configuration'
import Commander from '@/components/Commander/Commander.vue'
import AppLoading from '@/components/AppLoading.vue'
import FeatureKeyDebugger from '@/components/FeatureKeyDebugger/FeatureKeyDebugger.vue'
import NoNetwork from './components/NoNetwork.vue'
import { get } from 'lodash'
import store from '@/store'
import { FindShopPoint } from '@/api/shopPoint'
import { FindShopRebate } from '@/api/rebate'
import { usePermissions } from '@/use/permissions'
import { useNewsHint } from '@/use/useNewsHint'
import { setUserProperties, userPropertiesConfig } from '@/use/useGtag'
import { useRouter } from 'vue-router/composables'
import FeatureKeyInspector from './components/FeatureKeyInspector.vue'
import { useShop } from './use/shop'
import { useOrg } from './use/useOrg'
import WarningDialog from './components/Dialog/WarningDialog.vue'
import UnsavedLeaveDialog from './components/Dialog/UnsavedLeaveDialog.vue'
import { createVersionPolling } from 'version-polling'
import axios from 'axios'

export default defineComponent({
  name: 'App',
  components: { FeatureKeyInspector, Commander, FeatureKeyDebugger, AppLoading, NoNetwork, WarningDialog, UnsavedLeaveDialog },
  setup () {
    const { checkAction } = usePermissions()
    const isOnline = ref(navigator?.onLine)
    const router = useRouter()
    const { shopId } = useShop()
    const { orgId, orgName } = useOrg()
    const usePermissionsDebugger = computed(() =>
      get(store.getters, 'app/useSidebarSearch'),
    )
    const usePermissionsInspector = computed(() =>
      ['dev', 'uat'].includes(configuration('appRuntime')),
    )
    const pageChanging = computed(() => get(store.state, 'app.pageChanging'))

    // #region 版本更新檢測
    const useCheckVersion = computed(() =>
      ['dev', 'uat'].includes(configuration('appRuntime')),
    )

    // 保存 VersionPolling 實例的引用
    const versionPollingInstance = ref(null)
    const handleVersionCancel = () => {
      versionPollingInstance.value?.onCancel()
      showUpdateDialog.value = false
    }
    const handleVersionRefresh = () => {
      versionPollingInstance.value?.onRefresh()
      showUpdateDialog.value = false
    }

    const showUpdateDialog = ref(false)
    const newVersion = ref(null)

    // 建立 VersionPolling 實例並保存引用
    versionPollingInstance.value = createVersionPolling({
      vcType: 'etag',
      silent: !useCheckVersion.value,
      htmlFileUrl: `${location.origin}/index.html`,
      pollingInterval: 5 * 60 * 1000,
      onUpdate: async () => {
        try {
          // 檢測到更新後，額外請求 version.json 獲取版本資訊
          const response = await axios.get('/version.json', {
            headers: { 'Cache-Control': 'no-cache' },
          })
          const versionInfo = response.data

          console.log('版本更新', versionInfo)
          newVersion.value = versionInfo?.version || ''
        } catch (error) {
          newVersion.value = ''
        }
        showUpdateDialog.value = true
      },
    })
    // #endregion

    // TODO 棄用
    const getMarketingPermission = async (shopId) => {
      let marketingItem = {
        classTicket: false,
        coupon: false,
        point: false,
        cashback: false,
      }

      let pointCheck = false
      if (checkAction('admin.shopPoint.page')) {
        const [res, err] = await FindShopPoint({ shopId })
        if (res) pointCheck = get(res, 'pointClientId') !== null
        if (err) pointCheck = false
      }
      let cashbackCheck = false
      if (checkAction('admin.shopCashback.page')) {
        const [res, err] = await FindShopRebate({ shopId })
        if (res) cashbackCheck = Object.keys(res).length !== 0
        if (err) cashbackCheck = false
      }
      marketingItem = {
        classTicket: checkAction('admin.classTicket.page'),
        coupon:
          checkAction('admin.coupon.page') ||
          checkAction('admin.couponExchange.page'),
        point: pointCheck,
        cashback: cashbackCheck,
      }

      store.commit('permission/SET_MARKETINGITEM_PERMISSIONS', {
        key: 'marketingItem',
        data: marketingItem,
      })
    }

    const { checkUnreadArticles } = useNewsHint()

    onBeforeMount(async () => {
      if (!ls.getToken()) return
      await nextTick()

      await checkUnreadArticles()
    })

    onMounted(() => {
      // 網路連接時觸發
      window.addEventListener('online', updateOnlineStatus)
      // 網路斷開時觸發
      window.addEventListener('offline', updateOnlineStatus)

      function updateOnlineStatus () {
        isOnline.value = navigator?.onLine
        console.log('this.isOnline', isOnline.value)
      }
    })

    return {
      usePermissionsInspector,
      usePermissionsDebugger,
      getMarketingPermission,
      shopId,
      isOnline,
      pageChanging,
      router,
      newVersion,
      showUpdateDialog,
      handleVersionRefresh,
      handleVersionCancel,
    }
  },
  data: () => ({
    loaded: false,
  }),
  computed: {
    ...mapGetters(['shopPlanIsReady']),
  },
  watch: {
    shopPlanIsReady: {
      handler: function (newVal) {
        if (newVal) {
          setTimeout(() => {
            this.getMarketingPermission(this.shopId)
          }, 500)
        }
      },
    },
  },
  async mounted () {
    console.log('📥 App mounted')
    window.$message = this.$message
    console.log('window.gtag', window.gtag)

    console.log('appRuntime', configuration('appRuntime'))

    this.setTemp()
    // 重新整理前暫存 token user
    window.onbeforeunload = () => {
      this.storeTemp()
    }
    // onReady 的第一個 callback 只在「初次導航成功」時觸發。初次導航若失敗或被取消
    // （例如 401 攔截器在 guard 還沒 next() 之前就 push 到 Login），vue-router 會把
    // history 標記成 ready 但改走 readyErrorCbs；之後成功的導航因為 ready 已是 true
    // 不會再補觸發 readyCbs，loaded 於是永遠停在 false，AppLoading 蓋著畫面不會消失。
    // 這裡補上 errorCb，讓初次導航無論成功或失敗都能把畫面交還給 router-view。
    const markLoaded = () => {
      this.loaded = true
    }
    this.$router.onReady(markLoaded, markLoaded)

    setUserProperties(userPropertiesConfig.shopId, this.shopId)
  },
  methods: {
    configuration,
    storeTemp () {
      const token = this.$store.getters.token
      const user = this.$store.getters.user
      const shop = this.$store.getters.shop
      ls.setTemp({ token, user, shop })
    },

    setTemp () {
      const { token, user, shop } = ls.getTemp() || {
        token: ls.getToken(),
        user: ls.getUser(),
        shop: ls.getShop(),
      }
      this.$store.commit('auth/SET_TOKEN', token)
      this.$store.commit('user/SET_USER', user)
      this.$store.commit('user/SET_NOW_SHOP', shop)

      ls.removeTemp()
    },
  },
})
</script>

<style>
/* pagination */
.table-pagination {
  padding-bottom: 50px;
}

.el-scrollbar .el-scrollbar__bar {
  opacity: 1 !important;
}
html,
body,
#app {
  @apply h-full;
}
</style>
